import { useCallback, useEffect, useState } from 'react'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import firebase from 'infrastructure/firebase'
import {
  googleUserAtom,
  internalGoogleUserAtom,
  GoogleUserAtomValue,
} from 'infrastructure/googleDrive'
import {
  isRequestError,
  isPermanentError,
  getFlowResults,
  deleteFlow,
} from '../../contentService/utility'
import {
  ContentServiceFlowResults,
  ContentServiceFlowStatus,
  RequestError,
} from '../../contentService/types'
import {
  googleAuthOverrideAtom,
  prepareResultAtom,
  processResultAtom,
  selectedLessonIdsAtom,
} from './atoms'
import {
  startSlidesPrepare,
  getSlidesPrepareFlowId,
  startSlidesProcess,
  getSlidesProcessFlowId,
  fetchPrepareWorkerLessons,
} from './utils'
import type {
  SlidesPrepareResultData,
  SlidesProcessResultData,
  SlidesPrepareWorkerLessons,
} from './types'

const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/drive',
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/presentations',
]

/**
 * Hook to read Google auth state from the existing googleUserAtom.
 */
export function useGoogleAuth() {
  const googleUser = useAtomValue(googleUserAtom)
  const isAuthenticated = Boolean(googleUser?.credential?.accessToken)
  const userEmail = googleUser?.user?.email ?? null

  return { isAuthenticated, userEmail }
}

/**
 * Hook that returns a function to refresh the Google OAuth access token
 * by re-authenticating via signInWithPopup. Updates the atom and returns
 * the fresh access token.
 */
function useRefreshGoogleToken() {
  const setGoogleUser = useSetAtom(internalGoogleUserAtom)

  return useCallback(async (): Promise<string> => {
    const provider = new firebase.auth.GoogleAuthProvider()
    for (const scope of GOOGLE_SCOPES) {
      provider.addScope(scope)
    }

    const result = (await firebase
      .auth()
      .signInWithPopup(provider)) as GoogleUserAtomValue

    if (!result?.credential?.accessToken) {
      throw new Error('Failed to refresh Google access token.')
    }

    setGoogleUser(result)
    return result.credential.accessToken
  }, [setGoogleUser])
}

// --- Prepare hooks ---

/**
 * Hook to check for an existing prepare flow on mount.
 * Skips check if prepare results already exist in the atom.
 */
export function useExistingPrepareFlowId(treeId: string, versionId: string) {
  const prepareResult = useAtomValue(prepareResultAtom)
  const [existingFlowId, setExistingFlowId] = useState<string | undefined>()
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    if (prepareResult) {
      setIsChecking(false)
      return
    }

    getSlidesPrepareFlowId(treeId, versionId)
      .then((response) => {
        if (response?.data) {
          setExistingFlowId(response.data)
        }
      })
      .catch(() => {})
      .finally(() => setIsChecking(false))
  }, [treeId, versionId, prepareResult])

  const clearExistingFlowId = useCallback(() => {
    setExistingFlowId(undefined)
  }, [])

  return { existingFlowId, isChecking, clearExistingFlowId }
}

/**
 * Hook that returns a startFlowFunction compatible with StartAndMonitorFlow.
 * Handles 409 (flow already exists) by falling back to getSlidesPrepareFlowId.
 */
export function usePrepareStartFlow(treeId: string, versionId: string) {
  const refreshToken = useRefreshGoogleToken()
  const googleAuthOverride = useAtomValue(googleAuthOverrideAtom)

  return useCallback(async (): Promise<string> => {
    const accessToken = googleAuthOverride ?? (await refreshToken())

    try {
      const response = await startSlidesPrepare(treeId, versionId, accessToken)
      return response.data.flow_id
    } catch (error) {
      const err = error as Error & { status?: number }
      if (err.status === 409) {
        const flowIdResponse = await getSlidesPrepareFlowId(treeId, versionId)
        if (flowIdResponse?.data) {
          return flowIdResponse.data
        }
        throw new Error('Flow already exists but could not retrieve flow ID.')
      }
      throw error
    }
  }, [treeId, versionId, refreshToken, googleAuthOverride])
}

/**
 * Hook that returns a callback for when the prepare flow completes.
 * Fetches results via job-flow API and stores them in the prepareResultAtom.
 *
 * Two paths:
 * 1. flowResults provided (from StartAndMonitorFlow.onFlowComplete) → extract directly
 * 2. No flowResults (from useExistingFlowPoll) → fetch via getSlidesPrepareFlowId + getFlowResults
 */
export function usePrepareFlowComplete(treeId: string, versionId: string) {
  const setPrepareResult = useSetAtom(prepareResultAtom)
  const setSelectedLessonIds = useSetAtom(selectedLessonIdsAtom)
  const [error, setError] = useState<string | null>(null)

  const handleFlowComplete = useCallback(
    async (flowResults?: ContentServiceFlowResults) => {
      try {
        let workerResult: Record<string, unknown> | undefined

        // Path 1: extract from onFlowComplete callback data
        const callbackResult = flowResults?.data?.worker_results?.[0]?.result
        if (callbackResult) {
          workerResult = callbackResult as Record<string, unknown>
        } else {
          // Path 2: fetch via job-flow results API
          const flowIdResponse = await getSlidesPrepareFlowId(treeId, versionId)
          if (flowIdResponse?.data) {
            const results = await getFlowResults(flowIdResponse.data)
            if (!isRequestError(results)) {
              workerResult = results.data?.worker_results?.[0]?.result as
                | Record<string, unknown>
                | undefined
            }
          }
        }

        if (!workerResult) {
          setError('Prepare completed but no result data available.')
          return
        }

        // Transform raw worker result to SlidesPrepareResultData
        const rawLessons = workerResult.lessons as
          | Record<string, { slide_name?: string }>
          | undefined
        const prepareData: SlidesPrepareResultData = {
          tree_id: (workerResult.tree_id as string) ?? treeId,
          tree_ver: (workerResult.tree_ver as string) ?? versionId,
          directory_id: workerResult.directory_id as string,
          directory_name: (workerResult.directory_name as string) ?? '',
          directory_link: (workerResult.directory_link as string) ?? '',
          lessons: rawLessons
            ? Object.entries(rawLessons).map(([id, data]) => ({
                lesson_id: id,
                lesson_title: data.slide_name ?? id,
              }))
            : [],
        }

        setPrepareResult(prepareData)
        setSelectedLessonIds(
          new Set(prepareData.lessons.map((l) => l.lesson_id)),
        )
        setError(null)
      } catch (err) {
        setError(
          `Error fetching prepare results: ${err instanceof Error ? err.message : 'Unknown error'}`,
        )
      }
    },
    [treeId, versionId, setPrepareResult, setSelectedLessonIds],
  )

  const clearError = useCallback(() => setError(null), [])
  return { handleFlowComplete, error, clearError }
}

/**
 * Hook that returns a poll callback for monitoring an existing flow via PollingFlowStatus.
 * Triggers handleFlowComplete when the flow completes.
 * Clears the existing flow ID on permanent errors so the start button reappears.
 */
export function useExistingFlowPoll(
  handleFlowComplete: () => Promise<void>,
  clearExistingFlowId: () => void,
) {
  return useCallback(
    (flowStatus?: ContentServiceFlowStatus | RequestError) => {
      if (!flowStatus) return

      if (isRequestError(flowStatus)) {
        if (isPermanentError(flowStatus)) {
          clearExistingFlowId()
        }
        return
      }

      if (flowStatus.data.completed) {
        handleFlowComplete().then(() => clearExistingFlowId())
      }
    },
    [handleFlowComplete, clearExistingFlowId],
  )
}

// --- Process hooks ---

/**
 * Hook that returns a startFlowFunction for the process stage.
 * Fetches the full prepare worker result (lessons dict + directory_id)
 * and sends the required body to process/start.
 */
export function useProcessStartFlow(treeId: string, versionId: string) {
  const refreshToken = useRefreshGoogleToken()
  const googleAuthOverride = useAtomValue(googleAuthOverrideAtom)
  const selectedLessonIds = useAtomValue(selectedLessonIdsAtom)

  return useCallback(async (): Promise<string> => {
    const accessToken = googleAuthOverride ?? (await refreshToken())

    const { directoryId, lessons } = await fetchPrepareWorkerLessons(
      treeId,
      versionId,
    )

    // Filter to only selected lessons
    const filteredLessons: SlidesPrepareWorkerLessons = {}
    for (const [id, data] of Object.entries(lessons)) {
      if (selectedLessonIds.has(id)) filteredLessons[id] = data
    }

    try {
      const response = await startSlidesProcess(
        treeId,
        versionId,
        accessToken,
        directoryId,
        filteredLessons,
      )
      if (!response.data?.flow_id) {
        throw new Error('No flow ID received')
      }
      return response.data.flow_id
    } catch (error) {
      const err = error as Error & { status?: number }
      if (err.status === 409) {
        const flowIdResponse = await getSlidesProcessFlowId(treeId, versionId)
        if (flowIdResponse?.data) return flowIdResponse.data
        throw new Error('Flow already exists but could not retrieve flow ID.')
      }
      throw error
    }
  }, [treeId, versionId, refreshToken, googleAuthOverride, selectedLessonIds])
}

/**
 * Hook that returns a startFlowFunction for retrying only the failed lessons.
 * Fetches prepare worker result, filters to failed lesson IDs, deletes existing
 * process flow, then starts process. Clears processResult only after startSlidesProcess
 * succeeds so that on API failure the user keeps the failed-lessons list and can retry again.
 */
export function useRetryFailedLessons(treeId: string, versionId: string) {
  const refreshToken = useRefreshGoogleToken()
  const googleAuthOverride = useAtomValue(googleAuthOverrideAtom)
  const processResult = useAtomValue(processResultAtom)
  const setProcessResult = useSetAtom(processResultAtom)
  const setSelectedLessonIds = useSetAtom(selectedLessonIdsAtom)

  return useCallback(async (): Promise<string> => {
    if (!processResult?.errors?.length) {
      throw new Error('No failed lessons to retry')
    }
    const failedLessonIds = new Set(
      processResult.errors.map((e) => e.lesson_id).filter(Boolean),
    )
    setSelectedLessonIds(failedLessonIds)
    const accessToken = googleAuthOverride ?? (await refreshToken())

    const { directoryId, lessons } = await fetchPrepareWorkerLessons(
      treeId,
      versionId,
    )

    const filteredLessons: SlidesPrepareWorkerLessons = {}
    for (const [id, data] of Object.entries(lessons)) {
      if (failedLessonIds.has(id)) filteredLessons[id] = data
    }
    if (Object.keys(filteredLessons).length === 0) {
      throw new Error('No matching lesson data for failed lessons')
    }

    try {
      const processFlowId = await getSlidesProcessFlowId(
        treeId,
        versionId,
      ).catch(() => null)
      if (processFlowId?.data) await deleteFlow(processFlowId.data)
    } catch {
      // Best effort; proceed with start
    }

    const response = await startSlidesProcess(
      treeId,
      versionId,
      accessToken,
      directoryId,
      filteredLessons,
    )
    if (!response.data?.flow_id) {
      throw new Error('No flow ID received')
    }
    // Clear only after start succeeded so UI can show monitoring; on throw above we keep previous results
    setProcessResult(null)
    return response.data.flow_id
  }, [
    treeId,
    versionId,
    refreshToken,
    googleAuthOverride,
    processResult,
    setProcessResult,
    setSelectedLessonIds,
  ])
}

/**
 * Hook to check for an existing process flow on mount.
 * Skips check if process results already exist in the atom.
 */
export function useExistingProcessFlowId(treeId: string, versionId: string) {
  const processResult = useAtomValue(processResultAtom)
  const [existingFlowId, setExistingFlowId] = useState<string | undefined>()
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    if (processResult) {
      setIsChecking(false)
      return
    }

    getSlidesProcessFlowId(treeId, versionId)
      .then((response) => {
        if (response?.data) {
          setExistingFlowId(response.data)
        }
      })
      .catch(() => {})
      .finally(() => setIsChecking(false))
  }, [treeId, versionId, processResult])

  const clearExistingFlowId = useCallback(() => {
    setExistingFlowId(undefined)
  }, [])

  return { existingFlowId, isChecking, clearExistingFlowId }
}

/**
 * Hook that returns a callback for when the process flow completes.
 * Extracts the process result from the flow results and stores in processResultAtom.
 */
export function useProcessFlowComplete(treeId: string, versionId: string) {
  const setProcessResult = useSetAtom(processResultAtom)
  const [error, setError] = useState<string | null>(null)

  const handleFlowComplete = useCallback(
    async (flowResults?: ContentServiceFlowResults) => {
      try {
        let processData: SlidesProcessResultData | undefined

        // Path 1: extract from onFlowComplete callback data
        const workerResult = flowResults?.data?.worker_results?.[0]?.result
        if (workerResult) {
          processData = workerResult as SlidesProcessResultData
        } else {
          // Path 2: fetch via flow_id (existing flow poll path)
          const flowIdResponse = await getSlidesProcessFlowId(treeId, versionId)
          if (flowIdResponse?.data) {
            const flowResults = await getFlowResults(flowIdResponse.data)
            if (!isRequestError(flowResults)) {
              processData = flowResults.data?.worker_results?.[0]?.result as
                | SlidesProcessResultData
                | undefined
            }
          }
        }

        if (processData) {
          setProcessResult(processData)
          setError(null)
        } else {
          setError('Process completed but no result data available.')
        }
      } catch (err) {
        setError(
          `Error fetching process results: ${err instanceof Error ? err.message : 'Unknown error'}`,
        )
      }
    },
    [treeId, versionId, setProcessResult],
  )

  const clearError = useCallback(() => setError(null), [])
  return { handleFlowComplete, error, clearError }
}

/**
 * Reusable reset for the prepare stage only: deletes prepare flow id if active,
 * clears prepareResultAtom and selectedLessonIdsAtom. Optionally runs a
 * callback after (e.g. process reset) so the caller can compose "reset both".
 */
export function useResetPrepareStage(
  treeId: string,
  versionId: string,
  alsoResetProcess?: () => Promise<void>,
) {
  const setPrepareResult = useSetAtom(prepareResultAtom)
  const setSelectedLessonIds = useSetAtom(selectedLessonIdsAtom)
  const [isResetting, setIsResetting] = useState(false)

  const resetPrepareStage = useCallback(async () => {
    setIsResetting(true)
    try {
      const prepareFlowId = await getSlidesPrepareFlowId(
        treeId,
        versionId,
      ).catch(() => null)
      if (prepareFlowId?.data) await deleteFlow(prepareFlowId.data)
    } catch {
      // Best effort; clear state either way
    }
    setPrepareResult(null)
    setSelectedLessonIds(new Set())
    if (alsoResetProcess) await alsoResetProcess()
    setIsResetting(false)
  }, [
    treeId,
    versionId,
    setPrepareResult,
    setSelectedLessonIds,
    alsoResetProcess,
  ])

  return { resetPrepareStage, isResetting }
}

/**
 * Reusable reset for the process stage only: deletes process flow id if active,
 * clears processResultAtom.
 */
export function useResetProcessStage(treeId: string, versionId: string) {
  const setProcessResult = useSetAtom(processResultAtom)
  const [isResetting, setIsResetting] = useState(false)

  const resetProcessStage = useCallback(async () => {
    setIsResetting(true)
    try {
      const processFlowId = await getSlidesProcessFlowId(
        treeId,
        versionId,
      ).catch(() => null)
      if (processFlowId?.data) await deleteFlow(processFlowId.data)
    } catch {
      // Best effort; clear state either way
    }
    setProcessResult(null)
    setIsResetting(false)
  }, [treeId, versionId, setProcessResult])

  return { resetProcessStage, isResetting }
}
